import type { PaxtonHighlightArea } from '@/components/react-pdf-viewer/schema/paxton-highlight-area'
import type { ContentViewerSession } from '@/content-viewer/store/schemas'
import type { AppDispatch } from '@/store/store'
import type { UserFileRead } from '@/uppy-file-upload/schemas'
import { fetchAndStoreUserFiles } from '@/user_files/files/utils/fetch-and-store-user-files'
import type { MedChronReference } from '@/medical-chronology/references/store/schemas'
import { buildMedChronViewerSession } from './build-med-chron-viewer-session'
import { MedChronSourceProvider, type MedChronSourceRef } from './schemas'

export type OpenMedChronSourceViewerOptions = {
  legacyReference?: MedChronReference
  highlights?: PaxtonHighlightArea[]
  startingPage?: number
}

export type OpenMedChronSourceViewerResult =
  | {
      opened: true
      session: ContentViewerSession
    }
  | {
      opened: false
      error: string
    }

type OpenMedChronSourceViewerArgs = {
  source: MedChronSourceRef
  options?: OpenMedChronSourceViewerOptions
  loadedUserFilesById: Record<string, UserFileRead>
  dispatch: AppDispatch
}

export async function openMedChronSourceViewer(args: OpenMedChronSourceViewerArgs): Promise<OpenMedChronSourceViewerResult> {
  const { source, options = {}, loadedUserFilesById, dispatch } = args

  if (source.provider === MedChronSourceProvider.LEGACY) {
    const session = buildMedChronViewerSession({
      source,
      reference: options.legacyReference,
      highlights: options.highlights,
      startingPage: options.startingPage,
    })
    return session ? { opened: true, session } : { opened: false, error: 'Could not load this source file.' }
  }

  let userFile = loadedUserFilesById[source.source_id]
  if (!userFile) {
    const records = await fetchAndStoreUserFiles([source.source_id], dispatch)
    userFile = records[source.source_id]
  }

  const session = buildMedChronViewerSession({
    source,
    reference: options.legacyReference,
    userFile,
    highlights: options.highlights,
    startingPage: options.startingPage,
  })

  return session ? { opened: true, session } : { opened: false, error: 'Could not load this source file.' }
}
